/**
 * 0226 박현민
 * 엔드포인트 기점으로 구조화
 * Basekey는 스웨거 기준으로 BASE 넣고 인자는 추가하는 식으로 하시죠
 * 사용예시: QUERY_KEYS.wanted.adjustment(2026, 1)
 */
export const QUERY_KEYS = {
  // roster 엔드포인트
  roster: {
    schedule: (scheduleId?: string, groupId?: string) => [
      "ROSTER_SCHEDULE",
      scheduleId,
      groupId,
    ],
    /** GET /roster/issued_roster — 마감 스냅샷 (useSnapShot 등). `groupId`는 쿼리 `group_id`와 동일 */
    issuedRosterSnapshot: (year: number, month: number, groupId?: string) => [
      "ROSTER_ISSUED_ROSTER",
      year,
      month,
      groupId ?? "",
    ],
    /** GET /roster/issued_roster/me — 로그인 사용자 본인 행만 (useMyIssuedRoster) */
    myIssuedRoster: (year: number, month: number) => [
      "ROSTER_ISSUED_ROSTER_ME",
      year,
      month,
    ],
    prevTail: (
      year: number,
      month: number,
      scheduleId?: string,
      tailDays?: number,
      groupId?: string
    ) => ["ROSTER_PREV_TAIL", year, month, scheduleId, tailDays, groupId],
  },
  // nurses 엔드포인트
  nurses: {
    list: (groupId?: string) => ["NURSES", groupId],
    detail: (nurseId: string) => ["NURSES", "detail", nurseId],
    monthlyLimits: (groupId?: string | null, nurseId?: string | null) =>
      ["NURSES", "monthly-limits", groupId ?? "", nurseId ?? ""] as const,
    personnelBasicInfo: () => ["personnel-basic-info"],
  },
  /** GET /groups — 관리자 그룹(병동) 목록 */
  groups: {
    list: () => ["groups", "list"] as const,
    /** GET /groups/by-office?office_id= — 병동 목록(+ hn_id는 마스터/HN 일부만) */
    byOffice: (officeId: string) =>
      ["groups", "by-office", officeId] as const,
    /** invalidateQueries — 위 쿼리 office_id 무관 일괄 무효화 */
    byOfficeScope: () => ["groups", "by-office"] as const,
  },
  // wanted 엔드포인트
  wanted: {
    adjustment: (year: number, month: number) => [
      "wanted_adjustment",
      year,
      month,
    ],
    all: (groupId?: string) => ["wanted_all", groupId],
    status: (year: number, month: number, groupId?: string) => [
      "wanted_status",
      year,
      month,
      groupId,
    ],
    config: (year: number, month: number, groupId: string = "") => [
      "wanted_config",
      year,
      month,
      groupId,
    ],
    /** GET /wanted/{year}/{month}/shift-requests */
    shiftRequests: (year: number, month: number, groupId?: string) => [
      "wanted_shift_requests",
      year,
      month,
      groupId,
    ],
  },
  // 선호도 (원티드 작성)
  preferences: {
    latest: (year: number, month: number, groupId?: string) => [
      "preferences_latest",
      year,
      month,
      groupId,
    ],
    all: (year: number, month: number, groupId?: string) => [
      "preferences_all",
      year,
      month,
      groupId,
    ],
    schedule: (year: number, month: number, groupId?: string) => [
      "PREFERENCE_SCHEDULE",
      year,
      month,
      groupId,
    ],
  },

  /** GET /grade/config */
  grade: {
    config: (groupId?: string | null) => ["GRADE_CONFIG", groupId] as const,
  },
  //  설정
  config: {
    configData: (groupId: string) => ["configData", groupId],
    configVersions: (groupId: string) => ["configVersions", groupId],
    rosterConfigVersions: (groupId: string) => [
      "rosterConfigVersions",
      groupId,
    ],
  },
  auth: {
    switchGroupCandidates: () => ["auth", "switch-group", "candidates"],
  },
  shifts: {
    list: (groupId?: string) => ["SHIFTS", groupId],
    shiftManager: (groupId?: string, configVersion: string = "default") => [
      "shiftManager",
      groupId,
      configVersion,
    ],
  },
  // 공휴일
  dates: {
    holidays: (year: number, month: number) => ["dates_holidays", year, month],
  },
  // 문의하기 (contact/list, contact/write 무효화용)
  contact: {
    list: (page: number, pagesize: number) => ["contact_list", page, pagesize],
  },
  // 공지사항 (contact/notice/list, contact/notice/detail)
  notice: {
    list: (page: number, pagesize: number) => ["notice_list", page, pagesize],
    detail: (noticeId: number) => ["notice_detail", noticeId],
  },
  // 알림
  alarms: {
    count: () => ["alarms", "count"],
    list: () => ["alarms", "list"],
    listPopover: () => ["alarms", "list", "popover"],
    listModal: () => ["alarms", "list", "modal"],
  },
  // 주휴
  weeklyOff: {
    my: (year: number, month: number, nurseId?: string) => [
      "weeklyOff_my",
      year,
      month,
      nurseId,
    ],
  },
} as const;
